import { useRef, useState } from "react";

import { NAV, PHONE, SOCIALS, WHATSAPP_URL } from "../content/site";
import { useStickyHeader } from "../hooks/useStickyHeader";
import {
  type Lang,
  LANG_LABEL,
  langFromPath,
  localePath,
  navHref,
} from "../i18n/lang";

import { AnnouncementBar } from "./AnnouncementBar";
import { MobileMenu } from "./MobileMenu";

function Icon({ d, label }: { d: string; label: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-label={label}
      role="img"
    >
      <path d={d} />
    </svg>
  );
}
const ICON = {
  facebook:
    "M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.6 1.6-1.6h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.2H7.4V14h2.7v8h3.4z",
  x: "M17.5 3h3.1l-6.8 7.8L21.8 21h-6.3l-4.9-6.4L5 21H1.9l7.3-8.3L1.5 3h6.4l4.4 5.9L17.5 3zm-1.1 16.2h1.7L6.9 4.7H5.1l11.3 14.5z",
  instagram:
    "M12 7.3a4.7 4.7 0 1 0 0 9.4 4.7 4.7 0 0 0 0-9.4zm0 7.8a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2zm6-8a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0zM21 8.2c-.1-1.5-.4-2.8-1.5-3.9S17 2.9 15.6 2.8C14.1 2.7 9.9 2.7 8.4 2.8 7 2.9 5.7 3.2 4.6 4.3S3.2 6.7 3.1 8.2c-.1 1.5-.1 5.7 0 7.2.1 1.5.4 2.8 1.5 3.9s2.4 1.4 3.9 1.5c1.5.1 5.7.1 7.2 0 1.5-.1 2.8-.4 3.9-1.5s1.4-2.4 1.5-3.9c.1-1.5.1-5.7 0-7.2zm-2 8.7c-.3.8-.9 1.4-1.7 1.7-1.2.5-4 .4-5.3.4s-4.1.1-5.3-.4c-.8-.3-1.4-.9-1.7-1.7-.5-1.2-.4-4-.4-5.3s-.1-4.1.4-5.3c.3-.8.9-1.4 1.7-1.7 1.2-.5 4-.4 5.3-.4s4.1-.1 5.3.4c.8.3 1.4.9 1.7 1.7.5 1.2.4 4 .4 5.3s.1 4.1-.4 5.3z",
  youtube:
    "M23 7.2c-.3-1-1-1.8-2-2C19.2 4.7 12 4.7 12 4.7s-7.2 0-9 .5c-1 .3-1.8 1-2 2C.5 9 .5 12 .5 12s0 3 .5 4.8c.3 1 1 1.8 2 2 1.8.5 9 .5 9 .5s7.2 0 9-.5c1-.3 1.8-1 2-2 .5-1.8.5-4.8.5-4.8s0-3-.5-4.8zM9.7 15.1V8.9l6 3.1-6 3.1z",
  whatsapp:
    "M12 2a10 10 0 0 0-8.6 15l-1.3 4.8 5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 5.2 5.2 0 0 0 3.2.6 2.7 2.7 0 0 0 1.8-1.3c.2-.6.2-1.1.2-1.2-.1-.2-.3-.3-.5-.4z",
} as const;

const ICON_LINK = "text-ink transition-colors duration-200 hover:text-magenta";

export function Header() {
  const { lang, path } = langFromPath(window.location.pathname);
  const [open, setOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const { scrolled, condensed } = useStickyHeader();
  const other: Lang = lang === "en" ? "hi" : "en";
  const isActive = (href: string) =>
    href === "/" ? path === "/" : path === href || path.startsWith(`${href}/`);

  return (
    <header
      className={`sticky top-0 z-40 bg-sand/95 backdrop-blur-sm transition-shadow duration-300 ${
        scrolled
          ? "shadow-[0_1px_0_rgba(42,36,32,0.12),0_16px_40px_-28px_rgba(42,36,32,0.65)]"
          : "shadow-[0_1px_0_rgba(42,36,32,0.08)]"
      }`}
    >
      {/* Scrolling folds this header down to one slim bar. Each fold carries
          `inert` on exactly the condition that hides it, so a folded region's
          links are never left invisible but still tabbable.
          The announcement folds at EVERY width: on a phone it is a two-line
          message, and a sticky header that kept it would eat a fifth of the
          screen. */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-32 opacity-100"
        }`}
        inert={scrolled}
      >
        <AnnouncementBar />
      </div>
      {/* The utility row folds on desktop only - on a phone it carries the
          hamburger, so it stays (just tighter). `condensed` is false at phone
          widths, which is what makes the `inert` here safe. */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
          condensed ? "md:max-h-0 md:opacity-0" : "max-h-40 opacity-100"
        }`}
        inert={condensed}
      >
        <div
          className={`mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 transition-[padding] duration-300 md:px-10 ${
            scrolled ? "py-2" : "py-3"
          }`}
        >
          <a
            href={localePath("/", lang)}
            className="flex items-center gap-3"
            aria-label="Vihangam Yoga Singapore, home"
          >
            <img
              src="/logo.png"
              alt=""
              width="52"
              height="52"
              className={`rounded-full bg-white object-contain p-0.5 transition-[height,width] duration-300 ${
                scrolled ? "h-10 w-10" : "h-12 w-12 md:h-14 md:w-14"
              }`}
            />
            <span className="font-display leading-tight text-ink">
              <span className="block text-base md:text-lg">Vihangam Yoga</span>
              <span className="block text-[0.7rem] font-normal tracking-[0.2em] text-magenta uppercase">
                Singapore
              </span>
            </span>
          </a>

          <div className="hidden items-center gap-3 md:flex">
            <a
              href={PHONE.tel}
              className="text-sm font-medium text-ink hover:text-magenta"
            >
              {PHONE.display}
            </a>
            <a
              href={WHATSAPP_URL}
              className={ICON_LINK}
              target="_blank"
              rel="noreferrer"
            >
              <Icon d={ICON.whatsapp} label="WhatsApp" />
            </a>
            {SOCIALS.facebookUrl ? (
              <a
                href={SOCIALS.facebookUrl}
                className={ICON_LINK}
                target="_blank"
                rel="noreferrer"
              >
                <Icon d={ICON.facebook} label="Facebook" />
              </a>
            ) : null}
            {SOCIALS.twitterUrl ? (
              <a
                href={SOCIALS.twitterUrl}
                className={ICON_LINK}
                target="_blank"
                rel="noreferrer"
              >
                <Icon d={ICON.x} label="X" />
              </a>
            ) : null}
            {SOCIALS.instagramUrl ? (
              <a
                href={SOCIALS.instagramUrl}
                className={ICON_LINK}
                target="_blank"
                rel="noreferrer"
              >
                <Icon d={ICON.instagram} label="Instagram" />
              </a>
            ) : null}
            {SOCIALS.youtubeUrl ? (
              <a
                href={SOCIALS.youtubeUrl}
                className={ICON_LINK}
                target="_blank"
                rel="noreferrer"
              >
                <Icon d={ICON.youtube} label="YouTube" />
              </a>
            ) : null}
            <a
              href={localePath(path, other)}
              lang={other}
              className="ml-2 inline-flex min-h-8 items-center rounded-full border border-hairline-strong px-4 text-xs font-semibold text-ink transition-colors hover:border-magenta hover:text-magenta"
            >
              {LANG_LABEL[other]}
            </a>
            <a
              href={localePath("/donate", lang)}
              className="inline-flex min-h-8 items-center rounded-full bg-crimson px-4 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              Donate
            </a>
          </div>

          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setOpen(true)}
            className="min-h-11 min-w-11 text-ink md:hidden"
            aria-label="Open menu"
            aria-expanded={open}
          >
            <svg
              viewBox="0 0 24 24"
              width="26"
              height="26"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </div>

      <nav
        className="hidden border-t border-ink/10 md:block"
        aria-label="Primary"
      >
        {/* 24px here + each link's own 16px = the 40px page gutter, so "Home" aligns under the logo. */}
        <ul className="mx-auto flex max-w-6xl items-stretch px-6">
          {/* Once the rows above have folded, the mark slides in here so the
              condensed bar still says whose site this is. It is decorative
              in that state - the folded row keeps the real "home" link - so
              it is hidden from assistive tech rather than duplicating it. */}
          <li
            className={`flex items-center overflow-hidden transition-[max-width,opacity] duration-300 ${
              condensed ? "max-w-16 opacity-100" : "max-w-0 opacity-0"
            }`}
            aria-hidden="true"
          >
            <a href={localePath("/", lang)} tabIndex={-1} className="pr-3">
              <img
                src="/logo.png"
                alt=""
                width="32"
                height="32"
                className="h-8 w-8 rounded-full bg-white object-contain p-0.5"
              />
            </a>
          </li>
          {NAV.map((item) => (
            <li key={item.label} className="group relative">
              <a
                href={navHref(item.href, lang)}
                className={`flex min-h-11 items-center border-b-2 px-4 text-[0.82rem] font-medium tracking-wide whitespace-nowrap transition-colors duration-200 ${isActive(item.href) ? "border-magenta text-magenta" : "border-transparent text-ink hover:text-magenta"}`}
                aria-current={isActive(item.href) ? "page" : undefined}
                aria-haspopup={item.children ? "menu" : undefined}
              >
                {item.label}
                {item.children ? (
                  <span className="ml-1 text-[0.6rem]" aria-hidden>
                    ▾
                  </span>
                ) : null}
              </a>
              {item.children ? (
                <ul className="invisible absolute left-0 top-full z-30 min-w-56 rounded-xl border border-hairline bg-card py-2 opacity-0 shadow-lift transition-opacity duration-200 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                  {item.children.map((c) => (
                    <li key={c.label}>
                      <a
                        href={c.external ? c.href : localePath(c.href, lang)}
                        className="block px-4 py-2.5 text-[0.82rem] text-ink transition-colors hover:bg-ivory hover:text-magenta"
                        target={c.external ? "_blank" : undefined}
                        rel={c.external ? "noreferrer" : undefined}
                      >
                        {c.label}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
          <li className="ml-auto flex items-center pr-4">
            {/* "Register", not "Register for October": the ten nav links leave
                about 165px here, which the longer label overruns by a hair -
                it then wraps and the pill balloons into a two-line blob that
                breaks the nav band. whitespace-nowrap makes that failure
                impossible if a label ever changes again, and the announcement
                bar directly above already carries the dates. */}
            <a
              href={localePath("/register", lang)}
              className="rounded-full bg-magenta px-4 py-1.5 text-[0.8rem] font-semibold whitespace-nowrap text-white shadow-soft transition duration-300 hover:-translate-y-0.5 hover:bg-magenta-deep hover:shadow-lift"
            >
              Register
            </a>
          </li>
        </ul>
      </nav>
      <MobileMenu
        open={open}
        onClose={() => setOpen(false)}
        lang={lang}
        pathname={path}
        returnFocusTo={hamburgerRef}
      />
    </header>
  );
}
